import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PublicShell, Loading, EmptyState } from "@/components/shared";
import { NewsletterInline, ExitIntentModal } from "@/components/Newsletter";
import { ContentCard } from "@/components/ContentCard";
import api from "@/lib/api";

function readingTime(p) {
  const text = p.content || p.excerpt || p.dek || "";
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  if (!words) return "";
  return `${Math.max(1, Math.round(words / 200))} min read`;
}

function FilterRow({ label, options, value, onChange, testid }) {
  if (!options?.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testid}>
      <span className="text-xs font-bold uppercase tracking-wider text-muted">{label}</span>
      <button onClick={() => onChange("")} className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${!value ? "bg-accent text-white" : "bg-card text-muted hover:text-ink"}`}>All</button>
      {options.map((o) => (
        <button key={o} onClick={() => onChange(o)} className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${value === o ? "bg-accent text-white" : "bg-card text-muted hover:text-ink"}`}>{o}</button>
      ))}
    </div>
  );
}

export default function Blog() {
  const [company, setCompany] = useState("");
  const [region, setRegion] = useState("");
  const [sector, setSector] = useState("");
  const { data: facets } = useQuery({ queryKey: ["blog-facets"], queryFn: async () => (await api.get("/blog/facets")).data });
  const params = new URLSearchParams();
  if (company) params.set("company", company);
  if (region) params.set("region", region);
  if (sector) params.set("sector", sector);
  const { data, isLoading } = useQuery({ queryKey: ["blog", company, region, sector], queryFn: async () => (await api.get(`/blog?desk=true&${params.toString()}`)).data });
  const hasFilters = !!(company || region || sector);
  const { data: essays } = useQuery({ queryKey: ["blog-essays"], queryFn: async () => (await api.get("/blog?desk=false")).data, enabled: !hasFilters });

  return (
    <PublicShell>
      <ExitIntentModal />
      <section className="border-b border-border"><div className="mx-auto max-w-7xl px-5 py-14 lg:px-8">
        <p className="kicker text-xs text-accent">The Desk</p>
        <h1 className="mt-3 font-heading text-[clamp(2rem,5vw,3rem)] font-extrabold leading-tight text-ink">One real business story every morning</h1>
        <p className="mt-3 max-w-2xl text-muted">A real company, global or Indian, with the operating lesson drawn out for owners of Indian product businesses.</p>
        <div className="mt-6 max-w-xl" data-testid="blog-header-signup"><NewsletterInline source="index-header" /></div>
      </div></section>

      <section className="mx-auto max-w-7xl px-5 py-12 lg:px-8">
        <div className="mb-8 space-y-3">
          <FilterRow label="Region" options={facets?.regions} value={region} onChange={setRegion} testid="filter-region" />
          <FilterRow label="Sector" options={facets?.sectors} value={sector} onChange={setSector} testid="filter-sector" />
          <FilterRow label="Company" options={facets?.companies} value={company} onChange={setCompany} testid="filter-company" />
        </div>
        {isLoading ? <Loading /> : (data?.length ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {data.map((p) => (
              <ContentCard key={p.id} type="article" to={`/blog/${p.slug}`} testid={`blog-card-${p.id}`}
                data={{ category: p.company || p.sector, title: p.title, summary: p.dek || p.excerpt, reading_time: readingTime(p) }} />
            ))}
          </div>
        ) : <EmptyState title="No stories yet" subtitle="The daily stream starts soon." />)}

        {!hasFilters && essays?.length > 0 && (
          <div className="mt-16 border-t border-border pt-12" data-testid="blog-essays">
            <p className="kicker text-xs text-muted">Earlier essays</p>
            <h2 className="mt-2 font-heading text-2xl font-bold text-ink">Longer reads from the archive</h2>
            <div className="mt-6 divide-y divide-border border-y border-border">
              {essays.map((p) => (
                <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`essay-card-${p.id}`}
                  className="group flex items-baseline justify-between gap-4 py-4 transition-colors hover:bg-card">
                  <h3 className="font-heading text-base font-semibold text-ink group-hover:text-accent">{p.title}</h3>
                  <span className="whitespace-nowrap text-xs text-muted">Essay →</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>
    </PublicShell>
  );
}
